// Datenquellen: RSS/Atom-Feeds, Kalender (Google Kalender, iCal/ICS) und Tabellen (Excel/CSV hochladen, Google Sheets,
// CSV-Adresse). Liste mit Zustand des letzten Abrufs und Fehlermeldung, Anlegen/Bearbeiten mit Testabruf und Hilfe,
// wo die Adresse zu finden ist, Datei hochladen (jede Datei = neue Version), Daten ansehen, Versionen, Löschen.
import '../styles/datasources.css';
import type { DataSet } from '@disploraq/layouts';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../api/client';
import {
  imageUrl, qkDs, uploadTable, useDataSource, useDataSourceData, useDataSources, useVersions, type DataSource, type DataSourceKind, type DataSourceOptions,
  type DataSourceProvider, type TestResult,
} from '../api/datasources';
import { can, useMe } from '../api/queries';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Confirm, Dialog, Empty, Field, formatBytes, formatDateTime, PageHead, relativeTime, Spinner } from '../components/ui';
import { localeOf } from '../i18n';

const KIND_ICON: Record<DataSourceKind, string> = { feed: 'rss', calendar: 'calendar', table: 'table' };
const INTERVALS = [5, 15, 30, 60, 180, 360, 720, 1440];

/** Art der Eingabe im Dialog: bestimmt Art, Herkunft und Anbieter. */
type Variant = 'feed' | 'google_calendar' | 'ical' | 'upload' | 'google_sheets' | 'csv_url';
const VARIANTS: { id: Variant; kind: DataSourceKind; origin: 'url' | 'upload'; provider: DataSourceProvider; icon: string }[] = [
  { id: 'feed', kind: 'feed', origin: 'url', provider: 'generic', icon: 'rss' },
  { id: 'google_calendar', kind: 'calendar', origin: 'url', provider: 'google_calendar', icon: 'calendar' },
  { id: 'ical', kind: 'calendar', origin: 'url', provider: 'generic', icon: 'calendar' },
  { id: 'upload', kind: 'table', origin: 'upload', provider: 'generic', icon: 'upload' },
  { id: 'google_sheets', kind: 'table', origin: 'url', provider: 'google_sheets', icon: 'table' },
  { id: 'csv_url', kind: 'table', origin: 'url', provider: 'generic', icon: 'link' },
];
const variantOf = (s: DataSource): Variant => (s.origin === 'upload' ? 'upload' : s.kind === 'feed' ? 'feed' : s.kind === 'calendar'
  ? (s.provider === 'google_calendar' ? 'google_calendar' : 'ical') : s.provider === 'google_sheets' ? 'google_sheets' : 'csv_url');

function intervalLabel(t: (k: string, o?: Record<string, unknown>) => string, m: number): string {
  return m < 60 ? t('datasources.everyMin', { count: m }) : m < 1440 ? t('datasources.everyHour', { count: m / 60 }) : t('datasources.everyDay');
}

export function DataSourcesPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: list, isLoading } = useDataSources();
  const qc = useQueryClient();
  const toast = useToast();
  const manage = can(me, 'datasources.manage');
  const [edit, setEdit] = useState<DataSource | 'new' | null>(null);
  const [view, setView] = useState<DataSource | null>(null);
  const [del, setDel] = useState<DataSource | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadFor, setUploadFor] = useState<DataSource | null>(null);

  const refresh = async (s: DataSource) => {
    setBusy(s.id);
    try {
      const r = await api.post<DataSource>(`/datasources/${s.id}/refresh`);
      await qc.invalidateQueries({ queryKey: qkDs.list });
      await qc.invalidateQueries({ queryKey: qkDs.data(s.id) });
      if (r.status === 'error') toast.show(t('datasources.fetchFailed', { message: r.lastError ?? '' }));
      else toast.show(t('datasources.fetched'));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const upload = async (s: DataSource, file: File) => {
    setBusy(s.id);
    try {
      await uploadTable(s.id, file);
      await qc.invalidateQueries({ queryKey: qkDs.list });
      await qc.invalidateQueries({ queryKey: qkDs.data(s.id) });
      toast.show(t('datasources.uploaded'));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHead title={t('nav.datasources')} sub={t('datasources.sub')}
        actions={manage && <button className="btn primary" onClick={() => setEdit('new')}><Icon name="plus" />{t('datasources.new')}</button>} />
      <input ref={fileRef} type="file" hidden accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        data-testid="ds-file" onChange={(e) => {
          const f = e.target.files?.[0];
          if (f && uploadFor) void upload(uploadFor, f);
          e.target.value = '';
        }} />
      {isLoading ? <Spinner /> : !list?.length ? (
        <div className="card"><Empty icon="database" title={t('datasources.empty')} text={t('datasources.emptyText')}
          action={manage ? <button className="btn primary" onClick={() => setEdit('new')}><Icon name="plus" />{t('datasources.new')}</button> : undefined} /></div>
      ) : (
        <div className="ds-list">
          {list.map((s) => (
            <div key={s.id} className={`card ds-item${s.status === 'error' ? ' has-error' : ''}`} data-testid="ds-item">
              <div className="row">
                <span className="ds-ic"><Icon name={KIND_ICON[s.kind]} /></span>
                <div className="grow">
                  <b>{s.name}</b>
                  <div className="muted small">
                    {t(`datasources.variant.${variantOf(s)}`)}
                    {s.origin === 'url' ? ` · ${intervalLabel(t, s.intervalMinutes)}` : ''}
                    {!s.enabled ? ` · ${t('datasources.paused')}` : ''}
                  </div>
                </div>
                <span className={`pill ${s.status === 'ok' ? 'ok' : s.status === 'error' ? 'error' : 'warn'}`}>{t(`datasources.status.${s.status}`)}</span>
              </div>
              <div className="ds-facts small">
                <span>{t('datasources.items', { count: s.current?.itemCount ?? 0 })}</span>
                <span title={formatDateTime(s.lastSuccessAt, locale)}>{t('datasources.lastSuccess')}: {s.lastSuccessAt ? relativeTime(s.lastSuccessAt, locale) : '–'}</span>
                {s.lastChangeAt && <span title={formatDateTime(s.lastChangeAt, locale)}>{t('datasources.lastChange')}: {relativeTime(s.lastChangeAt, locale)}</span>}
                {s.origin === 'url' && s.enabled && s.nextFetchAt && <span>{t('datasources.nextFetch')}: {relativeTime(s.nextFetchAt, locale)}</span>}
                {s.current?.fileName && <span>{t('datasources.file')}: {s.current.fileName}</span>}
              </div>
              {s.status === 'error' && s.lastError && (
                <div className="ds-error small" role="alert">
                  <Icon name="alert" /><div><b>{t(`datasources.err.${s.lastErrorCode ?? 'other'}`, { defaultValue: t('datasources.err.other') })}</b>
                    <div>{s.lastError}{s.current ? ` ${t('datasources.keptLast')}` : ''}</div></div>
                </div>
              )}
              <div className="row ds-actions">
                <button className="btn sm ghost" onClick={() => setView(s)}><Icon name="list" />{t('datasources.showData')}</button>
                {manage && s.origin === 'url' && <button className="btn sm ghost" disabled={busy === s.id} onClick={() => void refresh(s)}><Icon name="refresh" />{t('datasources.refresh')}</button>}
                {manage && s.origin === 'upload' && <button className="btn sm ghost" disabled={busy === s.id} onClick={() => { setUploadFor(s); fileRef.current?.click(); }}>
                  <Icon name="upload" />{t('datasources.uploadNew')}</button>}
                {manage && <span className="ds-tools">
                  <button className="btn sm ghost icon" aria-label={t('common.edit')} title={t('common.edit')} onClick={() => setEdit(s)}><Icon name="edit" /></button>
                  <button className="btn sm ghost icon danger" aria-label={t('common.delete')} title={t('common.delete')} onClick={() => setDel(s)}><Icon name="trash" /></button>
                </span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {edit && <DataSourceDialog source={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
      {view && <DataDialog source={view} manage={manage} onClose={() => setView(null)} />}
      {del && (
        <Confirm danger title={t('common.delete')} text={t('datasources.confirmDelete', { name: del.name })} confirmLabel={t('common.delete')} onClose={() => setDel(null)}
          onConfirm={async () => {
            try {
              await api.del(`/datasources/${del.id}`);
              await qc.invalidateQueries({ queryKey: qkDs.list });
            } catch (e) {
              if (e instanceof ApiError && e.status === 409) toast.show(t('datasources.inUse', { names: e.message.replace(/^[^:]*:\s*/, '') }));
              else toast.error(e);
            }
            setDel(null);
          }} />
      )}
    </>
  );
}

/** Anlegen/Bearbeiten mit Hilfe zur Adresse und Testabruf. */
export function DataSourceDialog({ source, onClose }: { source: DataSource | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [variant, setVariant] = useState<Variant>(source ? variantOf(source) : 'feed');
  const v = VARIANTS.find((x) => x.id === variant)!;
  const [name, setName] = useState(source?.name ?? '');
  const [url, setUrl] = useState(source?.urlMasked ? '' : (source?.url ?? ''));
  const [interval, setIntervalMinutes] = useState(source?.intervalMinutes ?? 30);
  const [enabled, setEnabled] = useState(source?.enabled ?? true);
  const [opts, setOpts] = useState<DataSourceOptions>(source?.options ?? {});
  const [file, setFile] = useState<File | null>(null);
  const [test, setTest] = useState<TestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const setOpt = (p: Partial<DataSourceOptions>) => setOpts((o) => ({ ...o, ...p }));
  const needsUrl = v.origin === 'url';
  const valid = name.trim() && (!needsUrl || url.trim()) && (source || v.origin !== 'upload' || file);

  const runTest = async () => {
    setBusy(true);
    setTest(null);
    try {
      setTest(await api.post<TestResult>('/datasources/test', { kind: v.kind, url: url.trim(), provider: v.provider, options: opts }));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const submit = async () => {
    setBusy(true);
    try {
      let saved: DataSource;
      if (source) {
        saved = await api.patch<DataSource>(`/datasources/${source.id}`, {
          name: name.trim(), ...(needsUrl && url.trim() && url.trim() !== source.url ? { url: url.trim(), provider: v.provider } : {}),
          ...(needsUrl ? { intervalMinutes: interval, enabled } : {}), options: opts,
        }, { ifMatch: source.rowVersion });
      } else {
        saved = await api.post<DataSource>('/datasources', {
          name: name.trim(), kind: v.kind, origin: v.origin, ...(needsUrl ? { url: url.trim(), provider: v.provider, intervalMinutes: interval } : {}), options: opts,
        });
      }
      if (file && v.origin === 'upload') await uploadTable(saved.id, file);
      await qc.invalidateQueries({ queryKey: qkDs.list });
      await qc.invalidateQueries({ queryKey: qkDs.data(saved.id) });
      toast.show(t('common.saved'));
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog wide title={source ? source.name : t('datasources.new')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      {needsUrl && <button className="btn" disabled={!url.trim() || busy} onClick={() => void runTest()}>{t('datasources.test')}</button>}
      <button className="btn primary" disabled={!valid || busy} onClick={() => void submit()}>{t('common.save')}</button>
    </>}>
      <form className="col" onSubmit={(e) => { e.preventDefault(); if (valid) void submit(); }}>
        {!source && (
          <div className="ds-variants" role="radiogroup" aria-label={t('datasources.type')}>
            {VARIANTS.map((x) => (
              <button type="button" key={x.id} role="radio" aria-checked={variant === x.id} className={`ds-variant${variant === x.id ? ' on' : ''}`}
                onClick={() => { setVariant(x.id); setTest(null); setOpts({}); }}>
                <Icon name={x.icon} /><b>{t(`datasources.variant.${x.id}`)}</b><span className="small muted">{t(`datasources.variantSub.${x.id}`)}</span>
              </button>
            ))}
          </div>
        )}
        <Field label={t('common.name')}><input required value={name} onChange={(e) => setName(e.target.value)} placeholder={t(`datasources.namePh.${v.kind}`)} /></Field>
        {needsUrl && <>
          <Field label={t('datasources.url')} hint={source?.urlMasked ? t('datasources.urlHidden') : undefined}>
            <input value={url} onChange={(e) => { setUrl(e.target.value); setTest(null); }} placeholder={t(`datasources.urlPh.${variant}`)}
              autoComplete="off" spellCheck={false} inputMode="url" />
          </Field>
          <div className="ds-help small"><Icon name="key" /><div>{t(`datasources.help.${variant}`)}</div></div>
          <div className="two">
            <Field label={t('datasources.interval')}>
              <select value={interval} onChange={(e) => setIntervalMinutes(Number(e.target.value))}>
                {INTERVALS.map((m) => <option key={m} value={m}>{intervalLabel(t, m)}</option>)}
              </select>
            </Field>
            {source && <label className="check" style={{ alignSelf: 'end' }}><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />{t('datasources.enabled')}</label>}
          </div>
        </>}
        {v.origin === 'upload' && <>
          <Field label={source ? t('datasources.uploadNew') : t('datasources.file')} hint={t('datasources.help.upload')}>
            <input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid="ds-dialog-file" />
          </Field>
        </>}
        {v.kind === 'feed' && <label className="check"><input type="checkbox" checked={opts.images !== false} onChange={(e) => setOpt({ images: e.target.checked })} />{t('datasources.opt.images')}</label>}
        {v.kind === 'calendar' && <div className="two">
          <Field label={t('datasources.opt.days')}>
            <select value={opts.days ?? 14} onChange={(e) => setOpt({ days: Number(e.target.value) })}>
              {[7, 14, 31, 62].map((d) => <option key={d} value={d}>{t('datasources.opt.daysN', { count: d })}</option>)}
            </select>
          </Field>
          <label className="check" style={{ alignSelf: 'end' }}><input type="checkbox" checked={!!opts.showPrivate} onChange={(e) => setOpt({ showPrivate: e.target.checked })} />{t('datasources.opt.showPrivate')}</label>
        </div>}
        {v.kind === 'table' && <div className="two">
          <label className="check"><input type="checkbox" checked={opts.header !== false} onChange={(e) => setOpt({ header: e.target.checked })} />{t('datasources.opt.header')}</label>
          {v.origin === 'upload' && <Field label={t('datasources.opt.sheet')}><input value={opts.sheet ?? ''} onChange={(e) => setOpt({ sheet: e.target.value || undefined })} placeholder={t('datasources.opt.sheetPh')} /></Field>}
        </div>}
        {test && (
          <div className={`ds-test ${test.ok ? 'ok' : 'err'}`} role="status" data-testid="ds-test">
            {test.ok ? <><b><Icon name="check" /> {t('datasources.testOk', { count: test.itemCount })}</b>{test.data && <DataPreview data={test.data} compact />}</>
              : <><b><Icon name="alert" /> {t(`datasources.err.${test.error?.code ?? 'other'}`, { defaultValue: t('datasources.err.other') })}</b><div>{test.error?.message}</div></>}
          </div>
        )}
      </form>
    </Dialog>
  );
}

/** Daten ansehen, Verwendung, Versionen (Uploads: ältere Version wieder aktivieren). */
function DataDialog({ source, manage, onClose }: { source: DataSource; manage: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const qc = useQueryClient();
  const toast = useToast();
  const { data: detail } = useDataSource(source.id);
  const { data, isLoading } = useDataSourceData(source.id);
  const { data: versions = [] } = useVersions(source.id);
  const activate = async (no: number) => {
    try {
      await api.post(`/datasources/${source.id}/versions/${no}/activate`);
      await Promise.all([qc.invalidateQueries({ queryKey: qkDs.list }), qc.invalidateQueries({ queryKey: qkDs.data(source.id) }), qc.invalidateQueries({ queryKey: qkDs.versions(source.id) })]);
      toast.show(t('datasources.activated', { no }));
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog wide title={source.name} onClose={onClose} actions={<button className="btn" onClick={onClose}>{t('common.close')}</button>}>
      {isLoading ? <Spinner /> : !data ? <div className="muted">{t('datasources.noData')}</div> : (
        <>
          <div className="muted small">{t('datasources.version', { no: data.versionNo })} · {formatDateTime(data.fetchedAt, locale)}</div>
          <DataPreview data={data.data} />
        </>
      )}
      {detail && <div className="small" style={{ marginTop: 12 }}>
        <b>{t('datasources.usedBy')}:</b> {detail.usedBy.length ? detail.usedBy.map((u) => u.name).join(', ') : t('datasources.unused')}
      </div>}
      {versions.length > 1 && (
        <details style={{ marginTop: 12 }}>
          <summary>{t('datasources.versions', { count: versions.length })}</summary>
          <table className="list">
            <tbody>
              {versions.map((x) => (
                <tr key={x.id}>
                  <td>#{x.versionNo}</td><td className="muted">{formatDateTime(x.createdAt, locale)}</td><td className="muted">{x.fileName ?? t(`datasources.origin.${x.origin}`)}</td>
                  <td className="muted">{t('datasources.items', { count: x.itemCount })} · {formatBytes(x.sizeBytes, locale)}</td>
                  <td style={{ textAlign: 'right' }}>{x.current ? <span className="pill ok plain">{t('datasources.current')}</span>
                    : manage && <button className="btn sm ghost" onClick={() => void activate(x.versionNo)}>{t('datasources.activate')}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </Dialog>
  );
}

/** Normalisierte Daten als Liste bzw. Tabelle. */
export function DataPreview({ data, compact }: { data: DataSet; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const n = compact ? 5 : 50;
  if (data.kind === 'feed') {
    return (
      <div className="ds-preview">
        {data.title && <div className="muted small">{data.title}</div>}
        {data.items.slice(0, n).map((it, i) => (
          <div key={i} className="ds-feed-item">
            {it.image && !compact ? <img src={imageUrl(it.image)} alt="" loading="lazy" /> : null}
            <div><b>{it.title}</b>{it.date && <span className="muted small"> · {formatDateTime(it.date, locale)}</span>}
              {it.text && !compact && <div className="muted small">{it.text.slice(0, 240)}</div>}</div>
          </div>
        ))}
      </div>
    );
  }
  if (data.kind === 'calendar') {
    const when = (s: string, allDay: boolean) => (allDay ? new Date(`${s}T12:00:00Z`).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' }) + ` · ${t('datasources.allDay')}` : formatDateTime(s, locale));
    return (
      <div className="ds-preview">
        {!data.events.length && <div className="muted">{t('datasources.noEvents')}</div>}
        {data.events.slice(0, n).map((e) => (
          <div key={e.id} className="ds-feed-item"><div><span className="muted small">{when(e.start, e.allDay)}</span> <b>{e.title}</b>{e.location && <span className="muted small"> · {e.location}</span>}</div></div>
        ))}
      </div>
    );
  }
  return (
    <div className="ds-preview scroll">
      <table className="list">
        <thead><tr>{data.columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
        <tbody>{data.rows.slice(0, n).map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
      </table>
      {data.rows.length > n && <div className="muted small">{t('datasources.moreRows', { count: data.rows.length - n })}</div>}
      {data.truncated && <div className="muted small">{t('datasources.truncated')}</div>}
    </div>
  );
}
